import { TelemetryColorRanges, TelemetryIMShow, TelemetryIMShowParams } from '@shared/interfaces/telemetry';
import { MatrixDataPoint } from 'chartjs-chart-matrix';
import { ChartData, ChartOptions, CoreScaleOptions, Scale, TooltipItem } from 'chart.js';
import { getColorScale } from '../../../../utils/color/color';
import { createColorScaleLegendPlugin, createMatrixLabelsPlugin } from './plugins';

export const createHeatmap = (graph: TelemetryIMShow) => {
  const formats = Object.keys(graph.data);
  const matrixPoints = parseMatrixPoints(graph.data, formats);
  const maxValue = Math.max(...matrixPoints.map((p) => p.v || 0), 0);

  return {
    type: 'matrix' as const,
    title: graph.title,
    height: formats.length * 40,
    data: buildChartData(matrixPoints, maxValue, formats, graph.params.colorscale),
    options: createOptions(graph.params.labels, formats),
    plugins: [createMatrixLabelsPlugin(graph.params.colorscale), createColorScaleLegendPlugin(graph.params.colorscale)],
  };
};

const buildChartData = (
  matrixPoints: MatrixDataPoint[],
  maxValue: number,
  formats: string[],
  colors: TelemetryColorRanges,
): ChartData<'matrix', MatrixDataPoint[]> => {
  const data: ChartData<'matrix', MatrixDataPoint[]> = {
    datasets: [
      {
        data: matrixPoints,
        width: ({ chart }) => (chart.chartArea ? chart.chartArea.width / formats.length : 20),
        height: ({ chart }) => (chart.chartArea ? chart.chartArea.height / formats.length : 20),
        backgroundColor: (ctx) => {
          const raw = ctx.raw as MatrixDataPoint | undefined;
          const value = raw?.v || 0;
          const intensity = maxValue === 0 ? 0 : value / maxValue;
          return getColorScale(colors, intensity);
        },
      },
    ],
  };
  return data;
};

const parseMatrixPoints = (data: TelemetryIMShow['data'], formats: string[]): MatrixDataPoint[] => {
  return formats.flatMap((source, sourceIndex) =>
    formats.map((target, targetIndex) => ({
      x: targetIndex,
      y: sourceIndex,
      v: data[target][source] || 0,
    })),
  );
};

const createOptions = (labels: TelemetryIMShowParams['labels'], elements: string[]): ChartOptions<'matrix'> => {
  return {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    layout: {
      padding: { right: 70 },
    },
    scales: {
      x: {
        title: { text: labels.x, display: true, font: { weight: 'bold' } },
        type: 'linear',
        position: 'top',
        min: 0,
        max: elements.length - 1,
        ticks: {
          stepSize: 1,
          autoSkip: false,
          callback: (value: string | number) => elements[Number(value)] ?? '',
        },
        grid: { display: false },
      },
      y: {
        title: { text: labels.y, display: true, font: { weight: 'bold' } },
        type: 'linear',
        min: -0.5,
        max: elements.length - 0.5,
        reverse: true,
        afterBuildTicks: (axis: Scale<CoreScaleOptions>) => {
          axis.ticks = elements.map((_, i) => ({ value: i }));
        },
        ticks: {
          stepSize: 1,
          autoSkip: false,
          callback: (value: string | number) => elements[Number(value)] ?? '',
        },
        grid: { display: false },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<'matrix'>[]) => {
            const raw = items[0].raw as MatrixDataPoint;
            return `${elements[Number(raw.y)]} to ${elements[Number(raw.x)]}`;
          },
          label: (item: TooltipItem<'matrix'>) => {
            const raw = item.raw as MatrixDataPoint;
            return `${labels.color}: ${raw.v}`;
          },
        },
      },
      legend: { display: false },
    },
  };
};
